import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from "react-router-dom";
import { UserProfile, Key, Sheild, Cards, Card, Call } from '../CustomComponents/SwiftIcon/Icons';
import ServerRequest from '../../exports/ServerRequest';
import {
  isValidEmail,
  isValidPan,
  isValidPhoneNumber
} from "../../exports/UtilFunctions";
import Pulse from "../Loader/Pulse";
import AccessHeader from "./AccessHeader";
import { Alert } from "../Body/CustomChartComponents/CustomAlert/CustomAlert";

const warning_config = {
  Band: true,
  BandColor: "var(--main-blue)",
  BoxColor: "var(--white)",
  TextColor: "var(--text-color)",
  AutoClose: {
    Active: true,
    Line: true,
    LineColor: "var(--main-blue)",
    Time: 3,
  },
};

const RegisterSuperUser = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accountCode, setAccountCode] = useState("");
  const [otp, setOtp] = useState("");

  const [error1, setError1] = useState("");
  const [error2, setError2] = useState("");
  
  // Resend Timer State
  const [timer, setTimer] = useState(0);
  const [otpSent, setOtpSent] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    pan: "",
    mobile: "",
    countryCode: "+91",
  });

  const navigate = useNavigate();

  // Timer Logic
  useEffect(() => {
    const counter = timer > 0 && setInterval(() => setTimer(timer - 1), 1000);
    return () => clearInterval(counter);
  }, [timer]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSendOTP = async () => {
    if (timer > 0) return;
    if (!isValidEmail(formData.email)) {
      setError1("Please enter a valid email address");
      setError2("");
      return;
    }
    setLoading(true);
    try {
      const res = await ServerRequest({
        url: "/desktop/register/sendotp",
        method: "POST",
        data: { type: "email", value: formData.email }
      });
      if (!res.error) {
        setError1("");
        setError2("");
        setOtpSent(true);
        setTimer(30);
      } else {
        Alert({
          TitleText: "Warning",
          Message: res.message || "Failed to send OTP",
          ...warning_config,
        });
      }
    } catch (err) {
      Alert({
        TitleText: "Warning",
        Message: "Network error",
        ...warning_config,
      });
    }
    setLoading(false);
  };

  const handleVerifyOTP = async () => {
    if (otp.length < 4) {
      setError1("");
      setError2("Please enter correct OTP");
      return;
    }
    setLoading(true);
    try {
      const res = await ServerRequest({
        url: "/desktop/register/validateotp",
        method: "POST",
        data: { type: "email", value: formData.email, otp }
      });
      if (!res.error) {
        setError1("");
        setError2("");
        setOtpSent(false);
        setOtp("");
        setStep(2);
      } else {
        setError1("");
        setError2("Entered OTP is invalid");
      }
    } catch (err) {
      setError1("");
      setError2("Verification failed");
    }
    setLoading(false);
  };

  const handleValidatePAN = async () => {
    if (!isValidPan(formData.pan)) {
      setError1("");
      setError2("Please enter a valid PAN number");
      return;
    }
    setLoading(true);
    try {
      const res = await ServerRequest({
        url: "/desktop/register/validatepan",
        method: "POST",
        data: { type: "pan", value: formData.pan }
      });
      if (!res.error) {
        setError1("");
        setError2("");
        setStep(4);
      } else {
        Alert({
          TitleText: "Warning",
          Message: res.message || "Invalid PAN",
          ...warning_config,
        });
      }
    } catch (err) {
      Alert({
        TitleText: "Warning",
        Message: "Network error",
        ...warning_config,
      });
    }
    setLoading(false);
  };

  const handleFinalSubmit = async () => {
    if (!isValidPhoneNumber(formData.mobile)) {
      setError1("");
      setError2("Please enter a valid mobile number");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...formData,
        fullMobile: `${formData.countryCode} ${formData.mobile}`
      };
      
      const res = await ServerRequest({
        url: "/desktop/register",
        method: "POST",
        data: payload
      });
      
      if (res.error === false) {
        setAccountCode(res.account_code || "GENERATED");
        setStep(5);
      } else {
        Alert({
          TitleText: "Warning",
          Message: res.message || "Registration failed",
          ...warning_config,
        });
      }
    } catch (err) {
      Alert({
        TitleText: "Warning",
        Message: "Server error during registration",
        ...warning_config,
      });
    }
    setLoading(false);
  };

  function emailKeyUp(e) {
    if (e.key === "Enter") {
      handleSendOTP();
    }
  }

  function otpKeyUp(e) {
    if (e.key === "Enter") {
      handleVerifyOTP();
    }
  }

  function nameKeyUp(e) {
    if (e.key === "Enter") {
      setStep(3);
    }
  }

  function panKeyUp(e) {
    if (e.key === "Enter") {
      handleValidatePAN();
    }
  }

  function mobileKeyUp(e) {
    if (e.key === "Enter") {
      handleFinalSubmit();
    }
  }

  return (
    <div className="user-login">
      <div className="user-login-popup-wrapper">
        {loading ? (
          <>
            <div className="popup-loader">
              <Pulse />
            </div>
          </>
        ) : (
          <>
            <div className="login-header">
              <AccessHeader header_text="Account Creation" />
            </div>

            {/* Step 1: Email + OTP */}
            {step === 1 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="swift-input-box">
                      <p>Email</p>
                      <input
                        className="swift-input"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        onKeyUp={emailKeyUp}
                        placeholder="Your email address"
                      />
                      <p
                        className={
                          error1 === "" ? "error-text hide" : "error-text"
                        }
                      >
                        {error1}
                      </p>
                    </div>
                    <div className="swift-button-icon" onClick={handleSendOTP}>
                      <p>Generate OTP</p>
                    </div>
                    {otpSent && (
                      <>
                        <p
                          className={
                            timer === 0
                              ? "resend-message hide"
                              : "resend-message"
                          }
                        >
                          Please wait {timer} seconds to resend
                        </p>
                        <div className="swift-input-box">
                          <p>OTP</p>
                          <input
                            className="swift-input"
                            value={otp}
                            placeholder="123456"
                            onKeyUp={otpKeyUp}
                            maxLength={6}
                            onChange={(e) => {
                              setOtp(e.target.value.replace(/[^\d]/g, ""));
                            }}
                          />
                          <p
                            className={
                              error2 === "" ? "error-text hide" : "error-text"
                            }
                          >
                            {error2}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                  {otpSent && (
                    <>
                      <div className="login-footer">
                        <div
                          className="swift-button-icon btn-xl"
                          onClick={handleVerifyOTP}
                        >
                          <p>Next</p>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <div className="footer-line">
                  <p>
                    Already have an account?{" "}
                    <Link to="/access/login">Login</Link>
                  </p>
                </div>
              </>
            )}

            {/* Step 2: Full Name */}
            {step === 2 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="swift-input-box">
                      <p>Full Name or Company Name</p>
                      <input
                        className="swift-input"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        onKeyUp={nameKeyUp}
                        placeholder=""
                      />
                    </div>
                  </div>
                  <div className="login-footer">
                    <div
                      className="swift-button-icon btn-xl"
                      onClick={() => setStep(3)}
                    >
                      <p>Next</p>
                    </div>
                  </div>
                </div>
                <div className="footer-line">
                  <p>
                    Already have an account?{" "}
                    <Link to="/access/login">Login</Link>
                  </p>
                </div>
              </>
            )}

            {/* Step 3: PAN Number */}
            {step === 3 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="swift-input-box">
                      <p>PAN Number</p>
                      <input
                        className="swift-input"
                        type="text"
                        maxLength={10}
                        value={formData.pan}
                        onChange={(e) => handleInputChange("pan", e.target.value.toUpperCase())}
                        onKeyUp={panKeyUp}
                        placeholder=""
                      />
                      <p
                        className={
                          error2 === "" ? "error-text hide" : "error-text"
                        }
                      >
                        {error2}
                      </p>
                    </div>
                  </div>
                  <div className="login-footer">
                    <div
                      className="swift-button-icon btn-xl"
                      onClick={handleValidatePAN}
                    >
                      <p>Next</p>
                    </div>
                  </div>
                </div>
                <div className="footer-line">
                  <p>
                    Already have an account?{" "}
                    <Link to="/access/login">Login</Link>
                  </p>
                </div>
              </>
            )}

            {/* Step 4: Mobile Number */}
            {step === 4 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="swift-input-box">
                      <p>Mobile Number</p>
                      <input
                        className="swift-input"
                        maxLength={10}
                        type="text"
                        value={formData.mobile}
                        onChange={(e) => handleInputChange("mobile", e.target.value.replace(/[^\d]/g, ""))}
                        onKeyUp={mobileKeyUp}
                        placeholder="Your mobile number"
                      />
                      <p
                        className={
                          error2 === "" ? "error-text hide" : "error-text"
                        }
                      >
                        {error2}
                      </p>
                    </div>
                  </div>
                  <div className="login-footer">
                    <div
                      className="swift-button-icon btn-xl"
                      onClick={handleFinalSubmit}
                    >
                      <p>Register Now</p>
                    </div>
                  </div>
                </div>
                <div className="footer-line">
                  <p>
                    Already have an account?{" "}
                    <Link to="/access/login">Login</Link>
                  </p>
                </div>
              </>
            )}

            {/* Step 5: Success */}
            {step === 5 && (
              <>
                <div className="login-content">
                  <div className="login-body">
                    <div className="login-message">
                      <p>Account Created Successfully</p>
                      <span>Credentials have been dispatched to your email.</span>
                    </div>
                  </div>
                  <div className="login-footer">
                    <div
                      className="swift-button-icon btn-xl"
                      onClick={() => navigate("/")}
                    >
                      <p>Return to Login</p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RegisterSuperUser;